(() => {

function logError(message, ...args) {
  console.error(`[ProductCardVariant] ${message}`, ...args);
}

function logWarn(message, ...args) {
  console.warn(`[ProductCardVariant] ${message}`, ...args);
}

function isValidImageUrl(url) {
  return url &&
    typeof url === 'string' &&
    url.trim() !== '' &&
    url !== 'null' &&
    url !== 'undefined' &&
    !url.includes('undefined');
}

let currentVariantId = null;

function initializeProductCardVariants(container = document) {
  const productCards = container.querySelectorAll('.product-card[data-product-id]');

  productCards.forEach(productCard => {
    if (productCard.dataset.variantsInitialized === 'true') return;

    const dataScript = productCard.querySelector('script.product-card-data');
    if (!dataScript) {
      logWarn('Product card data script not found for product:', productCard.dataset.productId);
      return;
    }

    let productData;
    try {
      productData = JSON.parse(dataScript.textContent);
    } catch (e) {
      logError('Failed to parse product card data:', e);
      return;
    }

    const imageWrapper = productCard.querySelector('.product-card__image-wrapper');
    if (!imageWrapper) {
      logWarn('Image wrapper not found for product:', productData.id);
      return;
    }

    const defaultImage = imageWrapper.querySelector('.product-card__image--default img, .product-card__image--default picture img');
    const hoverImage = imageWrapper.querySelector('.product-card__image--hover img, .product-card__image--hover picture img');

    if (!defaultImage) {
      logWarn('Default image not found for product:', productData.id);
      return;
    }

    const colorSwatches = productCard.querySelectorAll('.color-swatch[data-variant-id]');
    const variantImages = productCard.querySelectorAll('.product-card__variant-image[data-variant-id]');
    const allVariantSelectors = [...colorSwatches, ...variantImages];

    function handleVariantSelect(e, selectedElement) {
      e.preventDefault();
      e.stopPropagation();

      const variantId = parseInt(selectedElement.dataset.variantId);
      const variant = productData.variants.find(v => v.id === variantId);

      if (!variant) {
        logWarn('Variant not found:', variantId);
        return;
      }

      const requestId = variantId;
      currentVariantId = variantId;

      const mediaIndex = variant.featured_media && variant.featured_media.media_index !== undefined
        ? variant.featured_media.media_index
        : 0;

      allVariantSelectors.forEach((el) => {
        el.classList.remove('active', 'border-black', 'ring-2', 'ring-black');
        if (el.classList.contains('product-card__variant-image')) {
          el.classList.add('border-gray-300');
        }
        el.setAttribute('aria-pressed', 'false');
      });
      selectedElement.classList.add('active');
      selectedElement.setAttribute('aria-pressed', 'true');
      if (selectedElement.classList.contains('product-card__variant-image')) {
        selectedElement.classList.remove('border-gray-300');
        selectedElement.classList.add('border-black');
      }

      syncProductLinksToVariant(productCard, variantId);

      updatePrice(productCard, variant);
      updateBadge(productCard, variant);

      if (defaultImage) {
        defaultImage.classList.add('opacity-50', 'transition-opacity', 'duration-300');
      }
      if (hoverImage) {
        hoverImage.classList.add('opacity-50', 'transition-opacity', 'duration-300');
      }

      (async () => {
        const imagePromises = [];
        let newImageUrl = null;

        if (variant.custom_featured_image && isValidImageUrl(variant.custom_featured_image)) {
          newImageUrl = variant.custom_featured_image;
          imagePromises.push(preloadImage(newImageUrl));
        }
        else if (variant.featured_media && isValidImageUrl(variant.featured_media.image_url)) {
          newImageUrl = variant.featured_media.image_url;
          imagePromises.push(preloadImage(newImageUrl));
        }
        else if (productData.media[mediaIndex] && isValidImageUrl(productData.media[mediaIndex].image_url)) {
          newImageUrl = productData.media[mediaIndex].image_url;
          imagePromises.push(preloadImage(newImageUrl));
        }

        if (hoverImage && productData.media.length > 1) {
          const nextIndex = (mediaIndex + 1) % productData.media.length;
          const nextImageUrl = productData.media[nextIndex]?.image_url;
          if (isValidImageUrl(nextImageUrl)) {
            imagePromises.push(preloadImage(nextImageUrl));
          }
        }

        try {
          await Promise.all(imagePromises);

          if (currentVariantId !== requestId) {
            return;
          }

          const swapPromises = [];

          if (newImageUrl) {
            swapPromises.push(
              updateImageSource(defaultImage, newImageUrl).then((success) => {
                if (currentVariantId !== requestId) return;
                if (success && defaultImage) {
                  defaultImage.classList.remove('opacity-50');
                }
              })
            );
          }

          if (hoverImage && productData.media.length > 1) {
            const nextIndex = (mediaIndex + 1) % productData.media.length;
            const nextImageUrl = productData.media[nextIndex]?.image_url;
            if (isValidImageUrl(nextImageUrl)) {
              swapPromises.push(
                updateImageSource(hoverImage, nextImageUrl).then((success) => {
                  if (currentVariantId !== requestId) return;
                  if (success) hoverImage.classList.remove('opacity-50');
                })
              );
            }
          }

          await Promise.all(swapPromises);
        } catch (error) {
          logError('Failed to update variant images:', error);

          if (defaultImage) {
            defaultImage.classList.remove('opacity-50');
          }
          if (hoverImage) {
            hoverImage.classList.remove('opacity-50');
          }
        }
      })();
    }

    colorSwatches.forEach((swatch) => {
      if (swatch.dataset.variantListenerAdded === 'true') return;
      swatch.addEventListener('click', (e) => handleVariantSelect(e, swatch));
      swatch.dataset.variantListenerAdded = 'true';
    });

    variantImages.forEach((btn) => {
      if (btn.dataset.variantListenerAdded === 'true') return;
      btn.addEventListener('click', (e) => handleVariantSelect(e, btn));
      btn.dataset.variantListenerAdded = 'true';
    });

    productCard.dataset.variantsInitialized = 'true';
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initializeProductCardVariants();
});

window.initializeProductCardVariants = initializeProductCardVariants;

function preloadImage(url, options = {}) {
  return new Promise((resolve, reject) => {
    if (!isValidImageUrl(url)) {
      reject(new Error(`Invalid image URL: ${url}`));
      return;
    }

    const img = new Image();
    const timeout = options.timeout || 10_000;

    let timeoutId = null;

    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId);
      img.onload = null;
      img.onerror = null;
    };

    img.onload = () => {
      cleanup();
      resolve(img);
    };

    img.onerror = (err) => {
      cleanup();
      logError('Image preload failed:', url, err);
      reject(new Error(`Failed to load image: ${url}`));
    };

    if (timeout > 0) {
      timeoutId = setTimeout(() => {
        cleanup();
        logWarn('Image preload timed out:', url);
        reject(new Error(`Image preload timeout: ${url}`));
      }, timeout);
    }

    img.src = url;
  });
}

function syncProductLinksToVariant(productCard, variantId) {
  if (!productCard || !variantId) return;

  const productLinks = productCard.querySelectorAll('a[href*="/products/"]');

  for (const link of productLinks) {
    try {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      url.searchParams.set('variant', variantId);
      link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
    } catch (error) {
      logWarn('Failed to sync variant id into product link href:', link.getAttribute('href'), error);
    }
  }
}

function updatePrice(productCard, variant) {
  if (!productCard || !variant) return;

  const priceContainer = productCard.querySelector('.product-card__price-container');
  if (!priceContainer) return;

  const priceElement = priceContainer.querySelector('.product-card__price');
  const comparePriceElement = priceContainer.querySelector('.product-card__compare-at-price');

  if (priceElement && variant.price_formatted) {
    priceElement.textContent = variant.price_formatted;
    if (priceElement.dataset.price !== undefined) {
      priceElement.dataset.price = variant.price;
    }
  }

  if (comparePriceElement) {
    const comparePrice = variant.compare_at_price || 0;

    if (comparePrice > variant.price) {
      comparePriceElement.textContent = variant.compare_at_price_formatted || '';
      comparePriceElement.classList.remove('hidden');
      if (comparePriceElement.dataset.compareAtPrice !== undefined) {
        comparePriceElement.dataset.compareAtPrice = comparePrice;
      }
    } else {
      comparePriceElement.classList.add('hidden');
      if (comparePriceElement.dataset.compareAtPrice !== undefined) {
        comparePriceElement.dataset.compareAtPrice = 0;
      }
    }
  }
}

// Point every candidate of a srcset at a new image while keeping its width and its
// descriptor ("1x", "2x", "1.5x", "750w"). Collapsing the list to one URL would leave
// HiDPI screens with only the 1x file after a swatch change.
function rebaseSrcset(srcset, baseImageUrl) {
  return srcset
    .split(',')
    .map((candidate) => candidate.trim())
    .filter(Boolean)
    .map((candidate) => {
      const [url, descriptor] = candidate.split(/\s+/);
      let next = baseImageUrl;

      const widthMatch = url.match(/[?&]width=(\d+)/);
      if (widthMatch) {
        next = /[?&]width=\d+/.test(next)
          ? next.replace(/([?&])width=\d+/, `$1width=${widthMatch[1]}`)
          : `${next}${next.includes('?') ? '&' : '?'}width=${widthMatch[1]}`;
      }

      const heightMatch = url.match(/[?&]height=(\d+)/);
      if (heightMatch) {
        next = /[?&]height=\d+/.test(next)
          ? next.replace(/([?&])height=\d+/, `$1height=${heightMatch[1]}`)
          : `${next}&height=${heightMatch[1]}`;
      }

      return descriptor ? `${next} ${descriptor}` : next;
    })
    .join(', ');
}

function updatePictureElement(picture, baseImageUrl) {
  if (!picture || !baseImageUrl) return;

  const sources = picture.querySelectorAll('source');

  sources.forEach(source => {
    const originalSrcset = source.getAttribute('srcset');
    if (!originalSrcset) return;

    source.srcset = rebaseSrcset(originalSrcset, baseImageUrl);
  });
}

function updateBadge(productCard, variant) {
  const imageWrapper = productCard.querySelector('.product-card__image-wrapper');
  if (!imageWrapper) return;

  const soldOutBadge = imageWrapper.querySelector('.product-card__badge--sold-out');
  const originalBadge = imageWrapper.querySelector('.product-card__badge:not(.product-card__badge--sold-out)');

  if (!variant.available) {
    if (originalBadge) originalBadge.classList.add('hidden');
    if (soldOutBadge) {
      soldOutBadge.classList.remove('hidden');
      soldOutBadge.classList.add('flex');
      soldOutBadge.removeAttribute('aria-hidden');
    }
  } else {
    if (soldOutBadge) {
      soldOutBadge.classList.remove('flex');
      soldOutBadge.classList.add('hidden');
      soldOutBadge.setAttribute('aria-hidden', 'true');
    }
    if (originalBadge) originalBadge.classList.remove('hidden');
  }
}

async function updateImageSource(imageElement, newUrl) {
  if (!imageElement) {
    logWarn('updateImageSource: No image element provided');
    return false;
  }

  if (!isValidImageUrl(newUrl)) {
    logWarn('updateImageSource: Invalid image URL:', newUrl);
    return false;
  }

  const img = imageElement.tagName === 'IMG'
    ? imageElement
    : imageElement.querySelector('img');

  if (!img) {
    logWarn('updateImageSource: No img element found');
    return false;
  }

  img.src = newUrl;

  const currentSrcset = img.getAttribute('srcset');
  if (currentSrcset && newUrl.includes('width=')) {
    img.srcset = rebaseSrcset(currentSrcset, newUrl);
  } else if (newUrl.includes('width=')) {
    const highResUrl = newUrl.replace(/width=\d+/, 'width=1000')
      .replace(/height=\d+/, 'height=1000');
    img.srcset = `${newUrl} 1x, ${highResUrl} 2x`;
  } else {
    img.srcset = '';
  }

  const picture = img.closest('picture');
  if (picture) {
    updatePictureElement(picture, newUrl);
  }

  try {
    if (typeof img.decode === 'function') {
      await img.decode();
    } else if (!img.complete) {
      await new Promise((resolve) => {
        const done = () => {
          img.removeEventListener('load', done);
          img.removeEventListener('error', done);
          resolve();
        };
        img.addEventListener('load', done);
        img.addEventListener('error', done);
      });
    }
  } catch (decodeError) {
    logWarn('Image decode failed; falling through to fade-in:', newUrl, decodeError);
  }

  return true;
}

})();
